import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IdeaLandscape as IdeaLandscapeType, IdeaCluster } from '../services/fieldUpdateService';
import IdeaClusterCard from './IdeaClusterCard';

interface Props {
  landscape: IdeaLandscapeType;
  onGenerateDraft: (selectedClusterIds: string[]) => void;
  generating: boolean;
}

const IdeaLandscape: React.FC<Props> = ({ landscape, onGenerateDraft, generating }) => {
  const { t } = useTranslation();

  const novel = landscape.clusters.filter(c => c.category === 'novel');
  const outliers = landscape.clusters.filter(c => c.category === 'outlier');
  const reinforcing = landscape.clusters.filter(c => c.category === 'reinforcing');

  // Default: novel selected, outliers + reinforcing not selected
  const [selected, setSelected] = useState<Set<string>>(() => new Set(novel.map(c => c.cluster_id)));
  const [reinforcingExpanded, setReinforcingExpanded] = useState(false);
  const [outliersExpanded, setOutliersExpanded] = useState(true);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = (clusters: IdeaCluster[]) =>
    setSelected(prev => { const n = new Set(prev); clusters.forEach(c => n.add(c.cluster_id)); return n; });
  const deselectAll = (clusters: IdeaCluster[]) =>
    setSelected(prev => { const n = new Set(prev); clusters.forEach(c => n.delete(c.cluster_id)); return n; });

  const selectedCount = selected.size;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {t('fieldUpdate.landscapeStats', '{{chunks}} relevant chunks → {{clusters}} clusters', {
            chunks: landscape.total_chunks_gated,
            clusters: landscape.total_clusters,
          })}
        </div>
        <span className="text-xs text-slate-400">{t('fieldUpdate.kb', 'KB')}: {landscape.kb_id.slice(0, 8)}…</span>
      </div>

      {/* Novel */}
      {novel.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-green-700 uppercase tracking-wide">
              {t('fieldUpdate.novelIdeas', 'Novel ideas ({{n}})', { n: novel.length })}
            </h3>
            <div className="flex gap-2 text-xs text-slate-500">
              <button onClick={() => selectAll(novel)} className="hover:text-blue-600">{t('common.selectAll', 'Select all')}</button>
              <span>·</span>
              <button onClick={() => deselectAll(novel)} className="hover:text-blue-600">{t('common.deselectAll', 'Deselect all')}</button>
            </div>
          </div>
          <div className="space-y-2">
            {novel.map(c => (
              <IdeaClusterCard key={c.cluster_id} cluster={c} selected={selected.has(c.cluster_id)} onToggle={toggle} />
            ))}
          </div>
        </section>
      )}

      {/* Outliers */}
      {outliers.length > 0 && (
        <section>
          <button
            onClick={() => setOutliersExpanded(v => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-amber-700 uppercase tracking-wide w-full text-left mb-2"
          >
            <span>{outliersExpanded ? '▼' : '▶'}</span>
            {t('fieldUpdate.outlierIdeas', 'Outlier ideas ({{n}}) — rare singletons for manual review', { n: outliers.length })}
          </button>
          {outliersExpanded && (
            <div className="space-y-2">
              {outliers.map(c => (
                <IdeaClusterCard key={c.cluster_id} cluster={c} selected={selected.has(c.cluster_id)} onToggle={toggle} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Reinforcing */}
      {reinforcing.length > 0 && (
        <section>
          <button
            onClick={() => setReinforcingExpanded(v => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wide w-full text-left mb-2"
          >
            <span>{reinforcingExpanded ? '▼' : '▶'}</span>
            {t('fieldUpdate.reinforcingIdeas', 'Reinforcing ideas ({{n}}) — already in your field', { n: reinforcing.length })}
          </button>
          {reinforcingExpanded && (
            <div className="space-y-2">
              {reinforcing.map(c => (
                <IdeaClusterCard key={c.cluster_id} cluster={c} selected={selected.has(c.cluster_id)} onToggle={toggle} />
              ))}
            </div>
          )}
        </section>
      )}

      <div className="pt-2 flex justify-end">
        <button
          onClick={() => onGenerateDraft(Array.from(selected))}
          disabled={generating || selectedCount === 0}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {generating
            ? t('fieldUpdate.generating', 'Generating draft...')
            : t('fieldUpdate.generateDraft', 'Generate updated draft ({{n}} selected)', { n: selectedCount })}
        </button>
      </div>
    </div>
  );
};

export default IdeaLandscape;
